<!--
  THE ONE VOLUME CONTROL A TRAILER GETS.

  Trailers that start themselves start silent (autoplay policy and common
  decency agree on that), so every surface that plays one — the hero banner, a
  focused rail tile — needs the same small way back to sound. This is that
  control, made once so the hero and the tiles cannot drift apart: a translucent
  glass circle holding the `speaker`/`mute` pair, whose glyphs share a cone so
  the toggle does not jump.

  It only flips state through the callback — the OWNER of the YouTube handle
  performs the actual mute()/unmute(), because unmuting must happen inside the
  click's own gesture for WebKit to honour it. Keep it that way: a component
  that reaches into a player it does not own is how two surfaces end up
  fighting over one iframe.

  It deliberately does not appear until the caller says a trailer is actually
  playing; a volume control over a still image is a lie.
-->
<script lang="ts">
  import Icon from './Icon.svelte';

  interface Props {
    /** Current state, owned by the caller (usually mirroring YtHandle.muted()). */
    muted: boolean;
    /** Flip it. Called inside the click gesture — do the unmute() right here. */
    ontoggle: () => void;
    /** Diameter in px. 34 sits quietly on a tile; the hero can afford 40. */
    size?: number;
    /** Optional: the caller manages the button's place in the tab order (a
     *  portal'd tile button has none of its own — see Poster.svelte). */
    onkeydown?: (event: KeyboardEvent) => void;
  }
  let { muted, ontoggle, size = 34, onkeydown }: Props = $props();
</script>

<button
  class="mute"
  style:--d="{size}px"
  aria-label={muted ? 'Turn sound on' : 'Turn sound off'}
  title={muted ? 'Turn sound on' : 'Turn sound off'}
  {onkeydown}
  onclick={(e) => {
    /* A trailer surface is often itself a link or a focus target; the volume
       press must never double as "open the title". */
    e.preventDefault();
    e.stopPropagation();
    ontoggle();
  }}
>
  <Icon name={muted ? 'mute' : 'speaker'} size={Math.round(size * 0.5)} />
</button>

<style>
  .mute {
    display: grid;
    place-items: center;
    width: var(--d);
    height: var(--d);
    padding: 0;
    border: 1px solid color-mix(in oklab, var(--text) 22%, transparent);
    border-radius: 50%;
    /* The same glass the badges wear, so one poster reads as one family. */
    background: color-mix(in oklab, var(--bg) 55%, transparent);
    backdrop-filter: blur(8px) saturate(1.3);
    box-shadow: var(--glass-shine);
    color: var(--text);
    cursor: pointer;
    opacity: 0.85;
    transition:
      opacity var(--dur) var(--ease),
      border-color var(--dur) var(--ease);
  }
  .mute:hover {
    opacity: 1;
  }
  .mute:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
    opacity: 1;
  }
  @media (pointer: coarse) {
    .mute { position: relative; }
    .mute::after { content: ""; position: absolute; width: max(100%, var(--hit)); height: max(100%, var(--hit)); top: 50%; left: 50%; transform: translate(-50%, -50%); }
  }
</style>
